import { useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface RowMenuItem {
  key: string
  label: string
  onSelect: () => void
  /** Separated below a divider and shown in red — Delete, never anything else. */
  danger?: boolean
}

/**
 * A row's "⋯" actions menu: named items rather than a strip of bare icon
 * buttons (design note §1 anno 5), with destructive actions set apart.
 */
export function RowMenu({ items }: { items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false)
  if (items.length === 0) return null

  const normal = items.filter((i) => !i.danger)
  const danger = items.filter((i) => i.danger)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size='icon'
          variant='ghost'
          className='h-7 w-7 text-muted-foreground'
          aria-label='More actions'
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='end'
        className='w-56 p-1'
        onClick={(e) => e.stopPropagation()}
      >
        {normal.map((item) => (
          <button
            key={item.key}
            type='button'
            className='flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none'
            onClick={() => {
              setOpen(false)
              item.onSelect()
            }}
          >
            {item.label}
          </button>
        ))}
        {danger.length > 0 && (
          <div className={cn(normal.length > 0 && 'mt-1 border-t pt-1')}>
            {danger.map((item) => (
              <button
                key={item.key}
                type='button'
                className='flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm text-destructive hover:bg-destructive/10 focus-visible:bg-destructive/10 focus-visible:outline-none'
                onClick={() => {
                  setOpen(false)
                  item.onSelect()
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
